/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AnagraficaFasiDTO,
  CampoEstraibileCSV,
  CollaudoConControlliRequest,
  ConfigurazioneCartellaProcedimento,
  CreaProcedimentoRequest,
  CreaUserTaskRequest,
  ErroreWS,
  GetVersioniProcedimentoParams,
  OggettiCollegatiResponse,
  ProcedimentoComunicazioniAttivabiliDTO,
  ProcedimentoDTO,
  UpdateIdFascicoloRequest,
  UpdateOggettiCollegatiRequest,
  UserTaskType,
  WsProblema,
  WsRispostaPaginataListProcedimentoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Dettaglio procedimento senza entità correlate
 * @summary Dettaglio procedimento
 */
export const getVersioniProcedimentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/versioni-procedimento/${id}`] as const;

export const getGetVersioniProcedimentoIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoId>>
  > = ({ signal }) => getVersioniProcedimentoId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoId>>
>;
export type GetVersioniProcedimentoIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio procedimento
 */
export const useGetVersioniProcedimentoId = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica un procedimento
 * @summary Modifica un procedimento
 */
export const putVersioniProcedimentoId = (
  id: string,
  creaProcedimentoRequest: CreaProcedimentoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: creaProcedimentoRequest,
    },
    options,
  );
};

export const getPutVersioniProcedimentoIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putVersioniProcedimentoId>>,
    TError,
    { id: string; data: CreaProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putVersioniProcedimentoId>>,
  TError,
  { id: string; data: CreaProcedimentoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putVersioniProcedimentoId>>,
    { id: string; data: CreaProcedimentoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putVersioniProcedimentoId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutVersioniProcedimentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putVersioniProcedimentoId>>
>;
export type PutVersioniProcedimentoIdMutationBody = CreaProcedimentoRequest;
export type PutVersioniProcedimentoIdMutationError = ErrorType<unknown>;

/**
 * @summary Modifica un procedimento
 */
export const usePutVersioniProcedimentoId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putVersioniProcedimentoId>>,
    TError,
    { id: string; data: CreaProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutVersioniProcedimentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Elimina un procedimento
 * @summary Elimina un procedimento
 */
export const deleteVersioniProcedimentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteVersioniProcedimentoIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteVersioniProcedimentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteVersioniProcedimentoId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteVersioniProcedimentoId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteVersioniProcedimentoId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteVersioniProcedimentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteVersioniProcedimentoId>>
>;

export type DeleteVersioniProcedimentoIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina un procedimento
 */
export const useDeleteVersioniProcedimentoId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteVersioniProcedimentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteVersioniProcedimentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce la lista delle cartelle configurate per il procedimento
 * @summary Lista cartelle procedimento
 */
export const getVersioniProcedimentoIdCartelle = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ConfigurazioneCartellaProcedimento[]>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/cartelle`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdCartelleQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/versioni-procedimento/${id}/cartelle`] as const;

export const getGetVersioniProcedimentoIdCartelleQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoIdCartelleQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>
  > = ({ signal }) =>
    getVersioniProcedimentoIdCartelle(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdCartelleQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>
>;
export type GetVersioniProcedimentoIdCartelleQueryError = ErrorType<unknown>;

/**
 * @summary Lista cartelle procedimento
 */
export const useGetVersioniProcedimentoIdCartelle = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCartelle>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdCartelleQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Salva/Aggiorna la configurazione della struttura a cartelle del procedimento per l'area pubblica
 * @summary Salva/Aggiorna la configurazione della struttura a cartelle del procedimento per l'area pubblica
 */
export const putVersioniProcedimentoIdCartelle = (
  id: string,
  configurazioneCartellaProcedimento: ConfigurazioneCartellaProcedimento[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ConfigurazioneCartellaProcedimento[]>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/cartelle`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: configurazioneCartellaProcedimento,
    },
    options,
  );
};

export const getPutVersioniProcedimentoIdCartelleMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdCartelle>>,
    TError,
    { id: string; data: ConfigurazioneCartellaProcedimento[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putVersioniProcedimentoIdCartelle>>,
  TError,
  { id: string; data: ConfigurazioneCartellaProcedimento[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdCartelle>>,
    { id: string; data: ConfigurazioneCartellaProcedimento[] }
  > = props => {
    const { id, data } = props ?? {};

    return putVersioniProcedimentoIdCartelle(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutVersioniProcedimentoIdCartelleMutationResult = NonNullable<
  Awaited<ReturnType<typeof putVersioniProcedimentoIdCartelle>>
>;
export type PutVersioniProcedimentoIdCartelleMutationBody =
  ConfigurazioneCartellaProcedimento[];
export type PutVersioniProcedimentoIdCartelleMutationError = ErrorType<unknown>;

/**
 * @summary Salva/Aggiorna la configurazione della struttura a cartelle del procedimento per l'area pubblica
 */
export const usePutVersioniProcedimentoIdCartelle = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdCartelle>>,
    TError,
    { id: string; data: ConfigurazioneCartellaProcedimento[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutVersioniProcedimentoIdCartelleMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna l'idFascicolo di un procedimento
 * @summary Aggiorna l'idFascicolo di un procedimento
 */
export const putVersioniProcedimentoIdAggiornaIdFascicolo = (
  id: string,
  updateIdFascicoloRequest: UpdateIdFascicoloRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/aggiornaIdFascicolo`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: updateIdFascicoloRequest,
    },
    options,
  );
};

export const getPutVersioniProcedimentoIdAggiornaIdFascicoloMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdAggiornaIdFascicolo>>,
    TError,
    { id: string; data: UpdateIdFascicoloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putVersioniProcedimentoIdAggiornaIdFascicolo>>,
  TError,
  { id: string; data: UpdateIdFascicoloRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdAggiornaIdFascicolo>>,
    { id: string; data: UpdateIdFascicoloRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putVersioniProcedimentoIdAggiornaIdFascicolo(
      id,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutVersioniProcedimentoIdAggiornaIdFascicoloMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdAggiornaIdFascicolo>>
  >;
export type PutVersioniProcedimentoIdAggiornaIdFascicoloMutationBody =
  UpdateIdFascicoloRequest;
export type PutVersioniProcedimentoIdAggiornaIdFascicoloMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiorna l'idFascicolo di un procedimento
 */
export const usePutVersioniProcedimentoIdAggiornaIdFascicolo = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putVersioniProcedimentoIdAggiornaIdFascicolo>>,
    TError,
    { id: string; data: UpdateIdFascicoloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutVersioniProcedimentoIdAggiornaIdFascicoloMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Dettaglio dei gruppi allegato assegnati ad un procedimento
 */
export const getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento =
  (
    idProcedimento: string,
    options?: SecondParameter<typeof apiClientRequest>,
    signal?: AbortSignal,
  ) => {
    return apiClientRequest<OggettiCollegatiResponse>(
      {
        url: `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/oggetti-collegati-procedimento`,
        method: 'get',
        signal,
      },
      options,
    );
  };

export const getGetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoQueryKey =
  (idProcedimento: string) =>
    [
      `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/oggetti-collegati-procedimento`,
    ] as const;

export const getGetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idProcedimento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoQueryKey(
        idProcedimento,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
        >
      >
    > = ({ signal }) =>
      getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento(
        idProcedimento,
        requestOptions,
        signal,
      );

    return { queryKey, queryFn, enabled: !!idProcedimento, ...queryOptions };
  };

export type GetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
      >
    >
  >;
export type GetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoQueryError =
  ErrorType<unknown>;

/**
 * @summary Dettaglio dei gruppi allegato assegnati ad un procedimento
 */
export const useGetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idProcedimento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const queryOptions =
      getGetVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoQueryOptions(
        idProcedimento,
        options,
      );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
      queryKey: QueryKey;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
  };

/**
 * @summary Sostituzione dei gruppi allegato assegnati ad un procedimento
 */
export const putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento =
  (
    idProcedimento: string,
    updateOggettiCollegatiRequest: UpdateOggettiCollegatiRequest,
    options?: SecondParameter<typeof apiClientRequest>,
  ) => {
    return apiClientRequest<string>(
      {
        url: `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/oggetti-collegati-procedimento`,
        method: 'put',
        headers: { 'Content-Type': 'application/json' },
        data: updateOggettiCollegatiRequest,
      },
      options,
    );
  };

export const getPutVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
        >
      >,
      TError,
      { idProcedimento: string; data: UpdateOggettiCollegatiRequest },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
      >
    >,
    TError,
    { idProcedimento: string; data: UpdateOggettiCollegatiRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
        >
      >,
      { idProcedimento: string; data: UpdateOggettiCollegatiRequest }
    > = props => {
      const { idProcedimento, data } = props ?? {};

      return putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento(
        idProcedimento,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PutVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
      >
    >
  >;
export type PutVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoMutationBody =
  UpdateOggettiCollegatiRequest;
export type PutVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoMutationError =
  ErrorType<unknown>;

/**
 * @summary Sostituzione dei gruppi allegato assegnati ad un procedimento
 */
export const usePutVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof putVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimento
        >
      >,
      TError,
      { idProcedimento: string; data: UpdateOggettiCollegatiRequest },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getPutVersioniProcedimentoIdProcedimentoOggettiCollegatiProcedimentoMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
/**
 * Elenco procedimenti senza entità correlate
 * @summary Elenco procedimenti
 */
export const getVersioniProcedimento = (
  params?: GetVersioniProcedimentoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoQueryKey = (
  params?: GetVersioniProcedimentoParams,
) =>
  [
    `/framework-cfg-procedimenti/versioni-procedimento`,
    ...(params ? [params] : []),
  ] as const;

export const getGetVersioniProcedimentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimento>>,
  TError = ErrorType<unknown>,
>(
  params?: GetVersioniProcedimentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimento>>
  > = ({ signal }) => getVersioniProcedimento(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetVersioniProcedimentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimento>>
>;
export type GetVersioniProcedimentoQueryError = ErrorType<unknown>;

/**
 * @summary Elenco procedimenti
 */
export const useGetVersioniProcedimento = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimento>>,
  TError = ErrorType<unknown>,
>(
  params?: GetVersioniProcedimentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un procedimento
 * @summary Crea un procedimento
 */
export const postVersioniProcedimento = (
  creaProcedimentoRequest: CreaProcedimentoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaProcedimentoRequest,
    },
    options,
  );
};

export const getPostVersioniProcedimentoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimento>>,
    TError,
    { data: CreaProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postVersioniProcedimento>>,
  TError,
  { data: CreaProcedimentoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postVersioniProcedimento>>,
    { data: CreaProcedimentoRequest }
  > = props => {
    const { data } = props ?? {};

    return postVersioniProcedimento(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostVersioniProcedimentoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postVersioniProcedimento>>
>;
export type PostVersioniProcedimentoMutationBody = CreaProcedimentoRequest;
export type PostVersioniProcedimentoMutationError = ErrorType<unknown>;

/**
 * @summary Crea un procedimento
 */
export const usePostVersioniProcedimento = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimento>>,
    TError,
    { data: CreaProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostVersioniProcedimentoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Valida un procedimento
 * @summary Valida un procedimento
 */
export const postVersioniProcedimentoIdValida = (
  id: string,
  userTaskType: UserTaskType[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ErroreWS[]>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/valida`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: userTaskType,
    },
    options,
  );
};

export const getPostVersioniProcedimentoIdValidaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdValida>>,
    TError,
    { id: string; data: UserTaskType[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postVersioniProcedimentoIdValida>>,
  TError,
  { id: string; data: UserTaskType[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdValida>>,
    { id: string; data: UserTaskType[] }
  > = props => {
    const { id, data } = props ?? {};

    return postVersioniProcedimentoIdValida(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostVersioniProcedimentoIdValidaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postVersioniProcedimentoIdValida>>
>;
export type PostVersioniProcedimentoIdValidaMutationBody = UserTaskType[];
export type PostVersioniProcedimentoIdValidaMutationError = ErrorType<unknown>;

/**
 * @summary Valida un procedimento
 */
export const usePostVersioniProcedimentoIdValida = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdValida>>,
    TError,
    { id: string; data: UserTaskType[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostVersioniProcedimentoIdValidaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Pubblica un procedimento
 * @summary Pubblica un procedimento
 */
export const postVersioniProcedimentoIdPubblica = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/pubblica`,
      method: 'post',
    },
    options,
  );
};

export const getPostVersioniProcedimentoIdPubblicaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdPubblica>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postVersioniProcedimentoIdPubblica>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdPubblica>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return postVersioniProcedimentoIdPubblica(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostVersioniProcedimentoIdPubblicaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postVersioniProcedimentoIdPubblica>>
>;

export type PostVersioniProcedimentoIdPubblicaMutationError =
  ErrorType<unknown>;

/**
 * @summary Pubblica un procedimento
 */
export const usePostVersioniProcedimentoIdPubblica = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdPubblica>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostVersioniProcedimentoIdPubblicaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Porta un procedimento in collaudo
 * @summary Porta un procedimento in collaudo
 */
export const postVersioniProcedimentoIdInCollaudo = (
  id: string,
  collaudoConControlliRequest: CollaudoConControlliRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/in-collaudo`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: collaudoConControlliRequest,
    },
    options,
  );
};

export const getPostVersioniProcedimentoIdInCollaudoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdInCollaudo>>,
    TError,
    { id: string; data: CollaudoConControlliRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postVersioniProcedimentoIdInCollaudo>>,
  TError,
  { id: string; data: CollaudoConControlliRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdInCollaudo>>,
    { id: string; data: CollaudoConControlliRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postVersioniProcedimentoIdInCollaudo(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostVersioniProcedimentoIdInCollaudoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postVersioniProcedimentoIdInCollaudo>>
>;
export type PostVersioniProcedimentoIdInCollaudoMutationBody =
  CollaudoConControlliRequest;
export type PostVersioniProcedimentoIdInCollaudoMutationError =
  ErrorType<unknown>;

/**
 * @summary Porta un procedimento in collaudo
 */
export const usePostVersioniProcedimentoIdInCollaudo = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdInCollaudo>>,
    TError,
    { id: string; data: CollaudoConControlliRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostVersioniProcedimentoIdInCollaudoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Associa task al procedimento
 * @summary Associa task al procedimento
 */
export const postVersioniProcedimentoIdProcedimentoTaskBpmn = (
  idProcedimento: string,
  creaUserTaskRequest: CreaUserTaskRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/task-bpmn`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaUserTaskRequest,
    },
    options,
  );
};

export const getPostVersioniProcedimentoIdProcedimentoTaskBpmnMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postVersioniProcedimentoIdProcedimentoTaskBpmn>
      >,
      TError,
      { idProcedimento: string; data: CreaUserTaskRequest },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdProcedimentoTaskBpmn>>,
    TError,
    { idProcedimento: string; data: CreaUserTaskRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postVersioniProcedimentoIdProcedimentoTaskBpmn>
      >,
      { idProcedimento: string; data: CreaUserTaskRequest }
    > = props => {
      const { idProcedimento, data } = props ?? {};

      return postVersioniProcedimentoIdProcedimentoTaskBpmn(
        idProcedimento,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostVersioniProcedimentoIdProcedimentoTaskBpmnMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdProcedimentoTaskBpmn>>
  >;
export type PostVersioniProcedimentoIdProcedimentoTaskBpmnMutationBody =
  CreaUserTaskRequest;
export type PostVersioniProcedimentoIdProcedimentoTaskBpmnMutationError =
  ErrorType<unknown>;

/**
 * @summary Associa task al procedimento
 */
export const usePostVersioniProcedimentoIdProcedimentoTaskBpmn = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoIdProcedimentoTaskBpmn>>,
    TError,
    { idProcedimento: string; data: CreaUserTaskRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostVersioniProcedimentoIdProcedimentoTaskBpmnMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Task associati al procedimento senza entità correlate
 * @summary Task associati al procedimento
 */
export const getVersioniProcedimentoIdTaskBpmn = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/task-bpmn`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdTaskBpmnQueryKey = (id: string) =>
  [
    `/framework-cfg-procedimenti/versioni-procedimento/${id}/task-bpmn`,
  ] as const;

export const getGetVersioniProcedimentoIdTaskBpmnQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoIdTaskBpmnQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>
  > = ({ signal }) =>
    getVersioniProcedimentoIdTaskBpmn(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdTaskBpmnQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>
>;
export type GetVersioniProcedimentoIdTaskBpmnQueryError = ErrorType<unknown>;

/**
 * @summary Task associati al procedimento
 */
export const useGetVersioniProcedimentoIdTaskBpmn = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdTaskBpmn>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdTaskBpmnQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Dettaglio procedimento con entità correlate
 * @summary Dettaglio procedimento con elenco di oggetti collegati
 */
export const getVersioniProcedimentoIdOggettiCollegati = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/oggetti-collegati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdOggettiCollegatiQueryKey = (
  id: string,
) =>
  [
    `/framework-cfg-procedimenti/versioni-procedimento/${id}/oggetti-collegati`,
  ] as const;

export const getGetVersioniProcedimentoIdOggettiCollegatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetVersioniProcedimentoIdOggettiCollegatiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>
  > = ({ signal }) =>
    getVersioniProcedimentoIdOggettiCollegati(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdOggettiCollegatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>
>;
export type GetVersioniProcedimentoIdOggettiCollegatiQueryError =
  ErrorType<unknown>;

/**
 * @summary Dettaglio procedimento con elenco di oggetti collegati
 */
export const useGetVersioniProcedimentoIdOggettiCollegati = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdOggettiCollegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdOggettiCollegatiQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Copia un procedimento
 * @summary Copia un procedimento
 */
export const getVersioniProcedimentoIdCopia = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/copia`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdCopiaQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/versioni-procedimento/${id}/copia`] as const;

export const getGetVersioniProcedimentoIdCopiaQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoIdCopiaQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>
  > = ({ signal }) =>
    getVersioniProcedimentoIdCopia(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdCopiaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>
>;
export type GetVersioniProcedimentoIdCopiaQueryError = ErrorType<unknown>;

/**
 * @summary Copia un procedimento
 */
export const useGetVersioniProcedimentoIdCopia = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCopia>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdCopiaQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Elenco campi estraibili csv (campi fissi del tipo domanda, richiesta e campi di tipo metadato/campi semantici delle sezioni). Endpoint richiamato da cfg-domande: serve lista campi fissi + metadati in configurazione di una domanda da estrarre in csv
 * @summary Elenco campi estraibili csv (campi fissi del tipo domanda, richiesta e campi di tipo metadato/campi semantici delle sezioni)
 */
export const getVersioniProcedimentoIdCampiGeojson = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CampoEstraibileCSV[]>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/campi-geojson`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdCampiGeojsonQueryKey = (id: string) =>
  [
    `/framework-cfg-procedimenti/versioni-procedimento/${id}/campi-geojson`,
  ] as const;

export const getGetVersioniProcedimentoIdCampiGeojsonQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetVersioniProcedimentoIdCampiGeojsonQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>
  > = ({ signal }) =>
    getVersioniProcedimentoIdCampiGeojson(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdCampiGeojsonQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>
>;
export type GetVersioniProcedimentoIdCampiGeojsonQueryError =
  ErrorType<unknown>;

/**
 * @summary Elenco campi estraibili csv (campi fissi del tipo domanda, richiesta e campi di tipo metadato/campi semantici delle sezioni)
 */
export const useGetVersioniProcedimentoIdCampiGeojson = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiGeojson>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdCampiGeojsonQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Elenco campi estraibili csv (campi fissi del tipo domanda, richiesta e campi di tipo metadato/campi semantici delle sezioni). Endpoint richiamato da cfg-domande: serve lista campi fissi + metadati in configurazione di una domanda da estrarre in csv
 * @summary Elenco campi estraibili csv (campi fissi del tipo domanda, richiesta e campi di tipo metadato/campi semantici delle sezioni)
 */
export const getVersioniProcedimentoIdCampiCsv = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CampoEstraibileCSV[]>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${id}/campi-csv`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdCampiCsvQueryKey = (id: string) =>
  [
    `/framework-cfg-procedimenti/versioni-procedimento/${id}/campi-csv`,
  ] as const;

export const getGetVersioniProcedimentoIdCampiCsvQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoIdCampiCsvQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>
  > = ({ signal }) =>
    getVersioniProcedimentoIdCampiCsv(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetVersioniProcedimentoIdCampiCsvQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>
>;
export type GetVersioniProcedimentoIdCampiCsvQueryError = ErrorType<unknown>;

/**
 * @summary Elenco campi estraibili csv (campi fissi del tipo domanda, richiesta e campi di tipo metadato/campi semantici delle sezioni)
 */
export const useGetVersioniProcedimentoIdCampiCsv = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoIdCampiCsv>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoIdCampiCsvQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce la fase della configurazione di un task comprensiva degli oggetti collegati tramite id processo bpmn (process definition id) ed id task bpmn.
 * @summary Restituisce la fase di un task tramite id processo bpmn (process definition id) ed id task bpmn.
 */
export const getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase = (
  idProcedimento: string,
  idTaskBpmn: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AnagraficaFasiDTO>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/id-task/${idTaskBpmn}/fase`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseQueryKey =
  (idProcedimento: string, idTaskBpmn: string) =>
    [
      `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/id-task/${idTaskBpmn}/fase`,
    ] as const;

export const getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idProcedimento: string,
    idTaskBpmn: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseQueryKey(
        idProcedimento,
        idTaskBpmn,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase
        >
      >
    > = ({ signal }) =>
      getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase(
        idProcedimento,
        idTaskBpmn,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(idProcedimento && idTaskBpmn),
      ...queryOptions,
    };
  };

export type GetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase
      >
    >
  >;
export type GetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce la fase di un task tramite id processo bpmn (process definition id) ed id task bpmn.
 */
export const useGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase = <
  TData = Awaited<
    ReturnType<typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase>
  >,
  TError = ErrorType<unknown>,
>(
  idProcedimento: string,
  idTaskBpmn: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFase
        >
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseQueryOptions(
      idProcedimento,
      idTaskBpmn,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce la fase della configurazione di un task comprensiva degli oggetti collegati tramite id processo bpmn in stato collaudo (process definition id) ed id task bpmn.
 * @summary Restituisce la fase di un task tramite id di un processo bpmn in stato collaudo (process definition id) ed id task bpmn.
 */
export const getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo =
  (
    idProcedimento: string,
    idTaskBpmn: string,
    options?: SecondParameter<typeof apiClientRequest>,
    signal?: AbortSignal,
  ) => {
    return apiClientRequest<AnagraficaFasiDTO>(
      {
        url: `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/id-task/${idTaskBpmn}/fase/in-collaudo`,
        method: 'get',
        signal,
      },
      options,
    );
  };

export const getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudoQueryKey =
  (idProcedimento: string, idTaskBpmn: string) =>
    [
      `/framework-cfg-procedimenti/versioni-procedimento/${idProcedimento}/id-task/${idTaskBpmn}/fase/in-collaudo`,
    ] as const;

export const getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudoQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idProcedimento: string,
    idTaskBpmn: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudoQueryKey(
        idProcedimento,
        idTaskBpmn,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
        >
      >
    > = ({ signal }) =>
      getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo(
        idProcedimento,
        idTaskBpmn,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(idProcedimento && idTaskBpmn),
      ...queryOptions,
    };
  };

export type GetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudoQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
      >
    >
  >;
export type GetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudoQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce la fase di un task tramite id di un processo bpmn in stato collaudo (process definition id) ed id task bpmn.
 */
export const useGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idProcedimento: string,
    idTaskBpmn: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudo
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const queryOptions =
      getGetVersioniProcedimentoIdProcedimentoIdTaskIdTaskBpmnFaseInCollaudoQueryOptions(
        idProcedimento,
        idTaskBpmn,
        options,
      );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
      queryKey: QueryKey;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
  };

/**
 * Recupera i tipi comunicazione attivabili a seconda dello stato in cui si trova l'istanza procedimento
 * @summary Recupera i tipi comunicazione attivabili a seconda dello stato in cui si trova l'istanza procedimento
 */
export const getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento =
  (
    id: string,
    idIstanzaProcedimento: string,
    options?: SecondParameter<typeof apiClientRequest>,
    signal?: AbortSignal,
  ) => {
    return apiClientRequest<ProcedimentoComunicazioniAttivabiliDTO>(
      {
        url: `/framework-cfg-procedimenti/versioni-procedimento/tipi-procedimento/${id}/tipi-comunicazioni-attivabili/${idIstanzaProcedimento}`,
        method: 'get',
        signal,
      },
      options,
    );
  };

export const getGetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimentoQueryKey =
  (id: string, idIstanzaProcedimento: string) =>
    [
      `/framework-cfg-procedimenti/versioni-procedimento/tipi-procedimento/${id}/tipi-comunicazioni-attivabili/${idIstanzaProcedimento}`,
    ] as const;

export const getGetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimentoQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
      >
    >,
    TError = ErrorType<unknown>,
  >(
    id: string,
    idIstanzaProcedimento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimentoQueryKey(
        id,
        idIstanzaProcedimento,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
        >
      >
    > = ({ signal }) =>
      getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento(
        id,
        idIstanzaProcedimento,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(id && idIstanzaProcedimento),
      ...queryOptions,
    };
  };

export type GetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimentoQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
      >
    >
  >;
export type GetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimentoQueryError =
  ErrorType<unknown>;

/**
 * @summary Recupera i tipi comunicazione attivabili a seconda dello stato in cui si trova l'istanza procedimento
 */
export const useGetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento =
  <
    TData = Awaited<
      ReturnType<
        typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
      >
    >,
    TError = ErrorType<unknown>,
  >(
    id: string,
    idIstanzaProcedimento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimento
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const queryOptions =
      getGetVersioniProcedimentoTipiProcedimentoIdTipiComunicazioniAttivabiliIdIstanzaProcedimentoQueryOptions(
        id,
        idIstanzaProcedimento,
        options,
      );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
      queryKey: QueryKey;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
  };
